/**
 * useSaveScreenState — everything SaveScreen knows besides its markup: the
 * Action/Decision choice and the routing-detection gate, the editable
 * transcript, the dirty-tracked judgment tags, the report destinations with the
 * client double-confirm, the photo context answers, the photo preview URLs, and
 * `canRoute`, the one place that decides whether Save is reachable.
 *
 * The rules these follow (AC-1g-04, -06, -14, -16, -17) are documented at the
 * top of SaveScreen.tsx and beside each piece of state below.
 */

import { useEffect, useRef, useState } from "preact/hooks";
import type { ActionDecisionValue } from "./ActionDecisionToggle";
import { serializeDest, type DestChoice } from "../triage/serialize-dest";
import type { DecisionMode } from "../triage/save-screen-types";
import type { SaveScreenProps } from "./SaveScreen";

export type SaveScreenStateDeps = Pick<
  SaveScreenProps,
  | "transcript"
  | "routingDetection"
  | "routingReviewPending"
  | "inferredRoutingSuggestionsEnabled"
  | "initialRoutingChoice"
  | "onRoutingChoiceChange"
  | "onTranscriptChange"
  | "hasPhotoPart"
  | "photoBlobs"
  | "saving"
  | "project_ref"
  | "onRouteAction"
  | "onRouteDecisionRaise"
  | "onRouteDecisionResolve"
  | "onCommitUntyped"
  | "onDestChange"
  | "onClientGuardFired"
  | "onContextChange"
  | "onJudgmentChange"
  | "initialContext"
  | "initialJudgment"
  | "initialDest"
  | "initialToggle"
  | "initialDecisionMode"
  | "destMode"
>;

export function useSaveScreenState({
  transcript,
  routingDetection: inferredRoutingDetection,
  routingReviewPending: inferredRoutingReviewPending = false,
  inferredRoutingSuggestionsEnabled = false,
  initialRoutingChoice,
  onRoutingChoiceChange,
  onTranscriptChange,
  hasPhotoPart,
  photoBlobs,
  saving = false,
  project_ref,
  onRouteAction,
  onRouteDecisionRaise,
  onRouteDecisionResolve,
  onCommitUntyped,
  onDestChange,
  onClientGuardFired,
  onContextChange,
  onJudgmentChange,
  initialContext,
  initialJudgment,
  initialDest,
  initialToggle,
  initialDecisionMode,
  destMode = "create",
}: SaveScreenStateDeps) {
  const routingDetection = inferredRoutingSuggestionsEnabled ? inferredRoutingDetection : undefined;
  const routingReviewPending = inferredRoutingSuggestionsEnabled && inferredRoutingReviewPending;
  const recoveredChoiceMatchesText = routingDetection !== undefined
    && routingDetection.reviewedText === transcript;
  const recoveredChoiceApplies = initialRoutingChoice === "log_only"
    || (initialRoutingChoice === "action" && routingDetection?.kinds.includes("action") === true)
    || (initialRoutingChoice === "decision" && routingDetection?.kinds.includes("decision") === true);
  const recoveredToggle = initialToggle ?? (
    recoveredChoiceMatchesText && recoveredChoiceApplies
      && (initialRoutingChoice === "action" || initialRoutingChoice === "decision")
      ? initialRoutingChoice
      : null
  );
  const [toggle, setToggle] = useState<ActionDecisionValue | null>(recoveredToggle);
  const [logOnly, setLogOnly] = useState(
    recoveredChoiceMatchesText && recoveredChoiceApplies && initialRoutingChoice === "log_only",
  );
  /* Did the human TAP the toggle on this visit, as opposed to it arriving
     pre-set from the saved entry?

     Seeding `toggle` fixed the display and broke the wire, and a test caught it:
     with the toggle pre-set, saving a typo fix on an Action entry dispatched
     through `onRouteAction`, so a text-only correction started re-asserting
     `route` on its carrier. Harmless-looking, and not harmless — a route on a
     carrier is what triggers the coordinator's Action fan-out (#8), so every
     edit of an Action entry would have poked the Action List.

     Display and intent are two different questions. This answers the second, and
     keeps the wire exactly where dirty-tracking already put it: absent means
     unchanged, so an untouched toggle emits no route and the entry keeps the one
     it has. A fresh capture is unaffected — nothing seeds it, so touching and
     being non-null are the same thing there. */
  const [toggleTouched, setToggleTouched] = useState(
    recoveredChoiceMatchesText && recoveredChoiceApplies
      && (initialRoutingChoice === "action" || initialRoutingChoice === "decision"),
  );
  // #32: editable transcript (rule #2 "confirm/correct"). Seeded ONCE from the
  // `transcript` prop; edits live here and live-mirror out via onTranscriptChange
  // so the caller carries the corrected value onto payload.transcript. Undefined
  // transcript (photo-only / video-pending) → the field is not rendered at all.
  const [transcriptDraft, setTranscriptDraft] = useState(transcript ?? "");

  function handleTranscriptInput(value: string) {
    setTranscriptDraft(value);
    onTranscriptChange?.(value);
    // A detector result and the human choice made in response to it are scoped
    // to the exact reviewed text. Never let a stale result route edited words.
    if (routingDetection && value !== routingDetection.reviewedText) {
      setToggle(null);
      setToggleTouched(false);
      setLogOnly(false);
      setDecisionMode(null);
      setSelectedDecisionRef(null);
      setRationale("");
      if (routingDetection.kinds.includes("material_change")) {
        setDestChoice({ kind: "untouched" });
        setClientPending(false);
        setClientConfirmed(false);
        onDestChange?.(serializeDest({ choice: { kind: "untouched" }, client: false }, destMode));
      }
      onRoutingChoiceChange?.(null);
    }
  }
  const [decisionMode, setDecisionMode] = useState<DecisionMode | null>(initialDecisionMode ?? null);
  const [selectedDecisionRef, setSelectedDecisionRef] = useState<string | null>(null);
  const [rationale, setRationale] = useState("");

  // Judgment fields (T-12, AC-1g-06): independent state, seeded ONLY with
  // literal empty/unset values — never read from `extraction`.
  const [criticalityTier, setCriticalityTier] = useState<string>(initialJudgment?.criticality_tier ?? "");
  const [ownerDraft, setOwnerDraft] = useState<string>(initialJudgment?.owner ?? "");
  // M-TAG1 (BUI-92): the first-tier tags. `phase` is a fixed picker (PHASE_OPTIONS
  // id), `trade` is free-text — both seeded ONLY with the literal re-open value,
  // never from `extraction`.
  const [phaseDraft, setPhaseDraft] = useState<string>(initialJudgment?.phase ?? "");
  const [tradeDraft, setTradeDraft] = useState<string>(initialJudgment?.trade ?? "");
  const [decidesDraft, setDecidesDraft] = useState<string>(initialJudgment?.decides ?? "");
  const [roomTagDraft, setRoomTagDraft] = useState<string>(initialJudgment?.room ?? "");

  // Destination flags (T-12, AC-1g-14): local UI state mirrored through
  // serializeDest() on every change, the single locus that maps this
  // state to the wire shape.
  const [destChoice, setDestChoice] = useState<DestChoice>(initialDest?.choice ?? { kind: "untouched" });

  // Client-report double-confirm (T-13, AC-1g-17). `clientPending` is the
  // FIRST tap's affirmative choice ONLY — it does not set anything on the
  // wire. `clientConfirmed` is set ONLY by the second, distinct confirm.
  const [clientPending, setClientPending] = useState(false);
  const [clientConfirmed, setClientConfirmed] = useState(initialDest?.client === true);

  // #39-save: object URLs for the in-flight `primary_photo` blob(s). Built ONCE
  // on mount (empty deps) and revoked on unmount — SaveScreen re-renders on every
  // transcript/context keystroke, and a capture's blobs are fixed while on this
  // screen, so rebuilding per-render would needlessly churn/flicker the URLs.
  // Guarded for jsdom, which lacks URL.createObjectURL (units stub it).
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  useEffect(() => {
    const canUrl = typeof URL !== "undefined" && typeof URL.createObjectURL === "function";
    if (!canUrl || !photoBlobs || photoBlobs.length === 0) return;
    const urls = photoBlobs.map((b) => URL.createObjectURL(b));
    setPhotoUrls(urls);
    return () => {
      if (typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
        urls.forEach((u) => URL.revokeObjectURL(u));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // T-10 context-block answers (AC-1g-16): required ONLY when
  // `hasPhotoPart` is true; unset/empty by default, human-typed only.
  const [contextRoom, setContextRoom] = useState(initialContext?.room ?? "");
  const [contextIntent, setContextIntent] = useState(initialContext?.intent ?? "");

  function emitContext(nextRoom: string, nextIntent: string) {
    onContextChange?.({ room: nextRoom, intent: nextIntent });
  }

  function handleContextRoomInput(value: string) {
    setContextRoom(value);
    emitContext(value, contextIntent);
  }

  function handleContextIntentInput(value: string) {
    setContextIntent(value);
    emitContext(contextRoom, value);
  }

  // §7.11 / judgment-emit: the human-set judgment fields (criticality tier +
  // owner) are live-mirrored to the caller (which owns Event construction),
  // exactly like dest/context. Null when unset/blank — NEVER seeded from
  // `extraction` (AC-1g-06). The design's Normal/Critical chips map onto the
  // shared `criticality_tier` vocabulary (Normal → "medium" standard,
  // Critical → "high") so the whole system — and the T3 exhaust — reasons over
  // ONE tier scale; the finer "low" tier is set only on the Master List detail.
  //
  // DIRTY-TRACKING (Red, 2026-07-30 — two supers on one project). Both fields are
  // seeded from `initialJudgment`, i.e. a snapshot of the record as this device
  // last read it. Emitting BOTH on every change meant that tapping a criticality
  // chip also re-asserted the owner as you last saw it — silently reverting a
  // colleague's owner change you never touched. The read can be 30s stale (the
  // dispatcher wakes on a timer), so that window is real.
  //
  // A field is emitted only once this human has touched IT, tracked here rather
  // than by comparing against the seed: a value can be edited back to what it was,
  // and that is still a deliberate assertion by this human, not a stale echo.
  // Absent means UNCHANGED downstream, so an untouched field simply leaves
  // whatever is there alone.
  const touched = useRef<{ criticality: boolean; owner: boolean; phase: boolean; trade: boolean; decides: boolean; room: boolean }>({
    criticality: false,
    owner: false,
    phase: false,
    trade: false,
    decides: false,
    room: false,
  });

  function emitJudgment(nextCriticality: string, nextOwner: string, nextPhase: string, nextTrade: string, nextDecides = decidesDraft, nextRoom = roomTagDraft) {
    onJudgmentChange?.({
      criticality_tier: !touched.current.criticality || nextCriticality === "" ? null : nextCriticality,
      owner: !touched.current.owner || nextOwner.trim() === "" ? null : nextOwner.trim(),
      // M-TAG1 (BUI-92): phase/trade ride the same dirty-tracked emit — absent
      // (null) until this human sets them, so an untouched tag never re-asserts
      // a stale value over a colleague's edit.
      phase: !touched.current.phase || nextPhase === "" ? null : nextPhase,
      trade: !touched.current.trade || nextTrade.trim() === "" ? null : nextTrade.trim(),
      decides: !touched.current.decides || nextDecides === "" ? null : nextDecides,
      room: !touched.current.room || nextRoom.trim() === "" ? null : nextRoom.trim(),
      decides_touched: touched.current.decides,
      room_touched: touched.current.room,
    });
  }

  function handleCriticalityChip(tier: string) {
    // Tapping the active chip again clears it — preserves the no-default rule
    // (AC-1g-06): nothing is selected until the human taps, and they can unset.
    const next = criticalityTier === tier ? "" : tier;
    setCriticalityTier(next);
    touched.current.criticality = true;
    emitJudgment(next, ownerDraft, phaseDraft, tradeDraft);
  }

  function handleOwnerInput(value: string) {
    setOwnerDraft(value);
    touched.current.owner = true;
    emitJudgment(criticalityTier, value, phaseDraft, tradeDraft);
  }

  // M-TAG1 (BUI-92): first-tier tag setters, mirroring the criticality/owner
  // pattern exactly — set state, mark touched, emit the whole judgment bag.
  function handlePhaseChange(value: string) {
    setPhaseDraft(value);
    touched.current.phase = true;
    emitJudgment(criticalityTier, ownerDraft, value, tradeDraft);
  }

  function handleTradeInput(value: string) {
    setTradeDraft(value);
    touched.current.trade = true;
    emitJudgment(criticalityTier, ownerDraft, phaseDraft, value);
  }

  function handleDecidesChange(value: string) {
    setDecidesDraft(value);
    touched.current.decides = true;
    emitJudgment(criticalityTier, ownerDraft, phaseDraft, tradeDraft, value, roomTagDraft);
  }

  function handleRoomTagInput(value: string) {
    setRoomTagDraft(value);
    touched.current.room = true;
    emitJudgment(criticalityTier, ownerDraft, phaseDraft, tradeDraft, decidesDraft, value);
  }

  const contextAnswered = contextRoom.trim().length > 0 && contextIntent.trim().length > 0;

  function emitDest(nextChoice: DestChoice, nextClient: boolean) {
    setDestChoice(nextChoice);
    onDestChange?.(serializeDest({ choice: nextChoice, client: nextClient }, destMode));
  }

  function replaceLogOnlyChoice() {
    if (!logOnly) return;
    setLogOnly(false);
    onRoutingChoiceChange?.(null);
  }

  function handleInternalOnlyToggle() {
    replaceLogOnlyChoice();
    // Internal-only is mutually exclusive with daily/weekly AND client
    // (§4.7): selecting it clears the others rather than composing.
    if (destChoice.kind === "internal_only") {
      emitDest({ kind: "untouched" }, clientConfirmed);
    } else {
      setClientPending(false);
      setClientConfirmed(false);
      emitDest({ kind: "internal_only" }, false);
    }
  }

  function handleDailyToggle() {
    replaceLogOnlyChoice();
    const base = destChoice.kind === "reports" ? destChoice : { kind: "reports" as const, daily: false, weekly: false };
    emitDest({ ...base, daily: !base.daily }, clientConfirmed);
  }

  function handleWeeklyToggle() {
    replaceLogOnlyChoice();
    const base = destChoice.kind === "reports" ? destChoice : { kind: "reports" as const, daily: false, weekly: false };
    emitDest({ ...base, weekly: !base.weekly }, clientConfirmed);
  }

  function handleClientFirstTap() {
    if (clientConfirmed) {
      // Already-set client flag: a tap here turns it OFF directly — the
      // double-confirm gate is for opting IN, not for removing a flag
      // the human already affirmed.
      setClientConfirmed(false);
      setClientPending(false);
      emitDest(destChoice, false);
      return;
    }
    // AC-1g-17: this tap is an affirmative CHOICE only — it opens the
    // second confirm and sets NOTHING on the wire.
    setClientPending(true);
  }

  function handleClientConfirmYes() {
    replaceLogOnlyChoice();
    setClientPending(false);
    setClientConfirmed(true);
    emitDest(destChoice, true);
    onClientGuardFired?.();
  }

  function handleClientConfirmCancel() {
    setClientPending(false);
    // clientConfirmed stays whatever it already was (false, since this
    // path is only reachable while not yet confirmed) — no flag is set.
  }

  // §7.11: Type (Action/Decision) is OPTIONAL — Save is blocked ONLY by the
  // integrity gates, never by the absence of a toggle. A HALF-FINISHED Decision
  // config still blocks (Decision picked with no raise/resolve mode, or resolve
  // without a picked candidate + typed rationale); an untyped capture
  // (toggle === null) is allowed and commits with NO route.
  const decisionConfigComplete =
    toggle !== "decision" ||
    decisionMode === "raise" ||
    (decisionMode === "resolve" && selectedDecisionRef !== null && rationale.trim().length > 0);

  // AC-1g-16 (T-10): a photo-bearing capture additionally requires the context
  // block answered — a context-free photo can never save. AC-1g-07
  // (TW-1g-PROJECT-INFER): a project_ref must be selected — a project-less
  // capture can never save (never reaches HELD/the wire). These integrity gates
  // plus a not-half-finished Decision config are the ONLY blockers.
  const activeRoutingDetection = routingDetection !== undefined
    && routingDetection.kinds.length > 0
    && routingDetection.reviewedText === transcriptDraft;
  const hasMaterialDestination = destChoice.kind === "internal_only"
    || (destChoice.kind === "reports" && (destChoice.daily || destChoice.weekly))
    || clientConfirmed;
  // A route must answer what the governed detector actually found. Merely
  // touching a different Type is still an explicit human action, but it is not
  // an applicable destination for the detection and therefore cannot dismiss
  // the gate. Log-only is the deliberate opt-out for every detected kind.
  const explicitRoutingChoice = logOnly
    || (toggle === "action" && routingDetection?.kinds.includes("action") === true)
    || (toggle === "decision" && routingDetection?.kinds.includes("decision") === true)
    || (routingDetection?.kinds.includes("material_change") === true && hasMaterialDestination);
  const canRoute = decisionConfigComplete
    && !routingReviewPending
    && (!activeRoutingDetection || explicitRoutingChoice)
    && (!hasPhotoPart || contextAnswered)
    && project_ref !== null;

  function handleToggleChange(value: ActionDecisionValue) {
    setToggle(value);
    setLogOnly(false);
    onRoutingChoiceChange?.(value);
    setToggleTouched(true);
    setDecisionMode(null);
    setSelectedDecisionRef(null);
    setRationale("");
  }

  function handleLogOnlyChoice() {
    setLogOnly(true);
    setToggle(null);
    setDestChoice({ kind: "untouched" });
    setClientPending(false);
    setClientConfirmed(false);
    onDestChange?.(serializeDest({ choice: { kind: "untouched" }, client: false }, destMode));
    setToggleTouched(false);
    setDecisionMode(null);
    setSelectedDecisionRef(null);
    setRationale("");
    onRoutingChoiceChange?.("log_only");
  }

  function handleSave() {
    if (!canRoute || saving) return;
    if (logOnly) {
      onCommitUntyped?.();
      return;
    }
    // A pre-set toggle SHOWS the entry's existing Type; it does not re-assert it.
    // See `toggleTouched`.
    const routing = toggleTouched ? toggle : null;
    if (routing === "action") {
      onRouteAction();
    } else if (routing === "decision" && decisionMode === "raise") {
      onRouteDecisionRaise();
    } else if (
      routing === "decision" &&
      decisionMode === "resolve" &&
      selectedDecisionRef !== null
    ) {
      onRouteDecisionResolve(selectedDecisionRef, rationale);
    } else {
      // §7.11: no toggle chosen → untyped capture, committed with NO route.
      onCommitUntyped?.();
    }
  }

  return {
    toggle,
    logOnly,
    transcriptDraft,
    decisionMode,
    setDecisionMode,
    selectedDecisionRef,
    setSelectedDecisionRef,
    rationale,
    setRationale,
    criticalityTier,
    ownerDraft,
    phaseDraft,
    tradeDraft,
    decidesDraft,
    roomTagDraft,
    destChoice,
    clientPending,
    clientConfirmed,
    photoUrls,
    contextRoom,
    contextIntent,
    routingReviewPending,
    activeRoutingDetection,
    explicitRoutingChoice,
    canRoute,
    handleTranscriptInput,
    handleContextRoomInput,
    handleContextIntentInput,
    handleCriticalityChip,
    handleOwnerInput,
    handlePhaseChange,
    handleTradeInput,
    handleDecidesChange,
    handleRoomTagInput,
    handleInternalOnlyToggle,
    handleDailyToggle,
    handleWeeklyToggle,
    handleClientFirstTap,
    handleClientConfirmYes,
    handleClientConfirmCancel,
    handleToggleChange,
    handleLogOnlyChoice,
    handleSave,
  };
}
